import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [admin, setAdmin] = useState(null);
    const [loading, setLoading] = useState(true);
    const [token, setToken] = useState(localStorage.getItem('nusatrade_token'));
    const [isAdmin, setIsAdmin] = useState(localStorage.getItem('nusatrade_is_admin') === 'true');

    useEffect(() => {
        if (token) {
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            fetchCurrentUser();
        } else {
            setLoading(false);
        }
    }, [token]);

    const fetchCurrentUser = async () => {
        try {
            if (isAdmin) {
                // For admin, we don't have a /me endpoint, so just verify token works
                setAdmin({ isAdmin: true });
            } else {
                const response = await axios.get(`${API}/auth/me`);
                setUser(response.data);
            }
        } catch (error) {
            console.error('Auth error:', error);
            logout();
        } finally {
            setLoading(false);
        }
    };

    const login = async (email, password) => {
        const response = await axios.post(`${API}/auth/login`, { email, password });
        const { token: newToken, user: userData } = response.data;
        localStorage.setItem('nusatrade_token', newToken);
        localStorage.setItem('nusatrade_is_admin', 'false');
        setToken(newToken);
        setIsAdmin(false);
        setUser(userData);
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        return userData;
    };

    const register = async (name, email, password, invitation_code, broker = '') => {
        const response = await axios.post(`${API}/auth/register`, { 
            name, 
            email, 
            password, 
            invitation_code,
            broker
        });
        const { token: newToken, user: userData } = response.data;
        localStorage.setItem('nusatrade_token', newToken);
        localStorage.setItem('nusatrade_is_admin', 'false');
        setToken(newToken);
        setIsAdmin(false);
        setUser(userData);
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        return userData;
    };

    const adminLogin = async (email, password) => {
        const response = await axios.post(`${API}/admin/auth/login`, { email, password });
        const { token: newToken, admin: adminData } = response.data;
        localStorage.setItem('nusatrade_token', newToken);
        localStorage.setItem('nusatrade_is_admin', 'true');
        setToken(newToken);
        setIsAdmin(true);
        setAdmin(adminData);
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        return adminData;
    };

    const adminRegister = async (name, email, password) => {
        const response = await axios.post(`${API}/admin/auth/register`, { name, email, password });
        const { token: newToken, admin: adminData } = response.data;
        localStorage.setItem('nusatrade_token', newToken);
        localStorage.setItem('nusatrade_is_admin', 'true');
        setToken(newToken);
        setIsAdmin(true);
        setAdmin(adminData);
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        return adminData;
    };

    const logout = () => {
        localStorage.removeItem('nusatrade_token');
        localStorage.removeItem('nusatrade_is_admin');
        setToken(null);
        setUser(null);
        setAdmin(null);
        setIsAdmin(false);
        delete axios.defaults.headers.common['Authorization'];
    };

    return (
        <AuthContext.Provider value={{
            user,
            admin,
            isAdmin,
            loading,
            token,
            login,
            register,
            adminLogin,
            adminRegister,
            logout,
            isAuthenticated: !!token
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
